import styled from 'styled-components';
import { useEffect, useState, useRef } from 'react';
import Alert from 'react-bootstrap/Alert';
import countryList from 'country-list';
import classNames from 'classnames';
import { device } from '../../../utils/styled-component-mixins';
import ButtonLink from '../../shared/ButtonLinkComponent';
import TextInput from '../../shared/FormComponents/TextInput';
import FormSelect from '../../shared/FormComponents/FormSelect';
import { withServerStyles } from '../../shared/ServerStylesConditionalWrapper';
import { compose } from '../../utils/compose';
import { useLocalization } from '../../custom-hooks/useLocalization';
import { withLocalization } from '../../shared/Context/LocalizationContext';
import { STATES } from '../../../constants/states';

const ManageAccountFormStyled = styled.div`
  .alert {
    margin-left: 1rem;
    position: fixed;
    right: 1rem;
    top: 8rem;
    z-index: 100;

    @media ${device.desktop} {
      right: 2rem;
      top: 15rem;
    }
  }

  .form-fields-wrapper {
    display: grid;
    grid-column-gap: 1.6rem;
    grid-row-gap: 2.5rem;
    grid-template-columns: 1fr;
    margin-bottom: 5rem;
    margin-top: 3rem;

    @media ${device.desktop} {
      grid-template-columns: repeat(2, 1fr);
    }

    .form-field {
      flex: 1 1 100%;
      margin-top: 0;
      position: relative;

      @media ${device.desktopXl} {
        flex: 1 1 30%;
      }

      #password-strength {
        position: absolute;
        top: -4.5rem;
        width: max-content;
      }
    }

    .custom-checkbox {
      flex: 1 1 100%;
    }
  }

  .buttons-wrapper {
    column-gap: 3rem;
    display: flex;
    flex-direction: column;
    margin-bottom: 3rem;
    row-gap: 1.6rem;

    @media ${device.desktop} {
      flex-direction: row;
    }

    button,
    a {
      @media ${device.mobileOnly} {
        justify-content: center;
        width: 100%;
      }
    }
  }
`;

const DEFAULT_STATE = {
  emailError: false,
  zipCodeError: false,
  disableSubmit: true
};

const ManageAccountForm = (props) => {
  const [state, setstate] = useState(DEFAULT_STATE);
  const [snackbarVariant, setSnackbarVariant] = useState('success');
  const [snackbarMessage, setSnackbarMessage] = useState(props.successMessage);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [userModel, setUserModel] = useState({ ...props.userProfileViewModel });
  const { resourceStrings } = useLocalization();
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [selectedState, setSelectedState] = useState(
    props.userProfileViewModel.state ? props.userProfileViewModel.state : ''
  );
  const [selectedCountry, setSelectedCountry] = useState('');
  const recaptchaRef = useRef(null);
  const hiddenSubmitRef = useRef(null);
  const errorMessagesRef = useRef(null);
  const deleteRef = useRef(null);

  const setState = (newState) => setstate({ ...state, ...newState });

  const showAlert = (message, variant) => {
    setSnackbarMessage(message);
    setSnackbarVariant(variant);
    setShowSnackbar(true);

    setTimeout(() => {
      setShowSnackbar(false);
    }, 5000);
  };

  useEffect(() => {
    if (props.isSaved && props.messages?.length > 0) {
      props.messages.forEach((message) => showAlert(message, 'success'));
    }

    if (!props.isSaved && props.messages?.length > 0) {
      props.messages.forEach((message) => showAlert(message, 'danger'));
    }
  }, [props.isSaved, props.messages]);

  const handleZipCodeInput = (event) => {
    const zipCodeError = { zipCodeError: false };
    event.target.setCustomValidity('');

    if (!event.target.value.match(/^[0-9]{5}$/)) {
      zipCodeError.zipCodeError = true;
      event.target.setCustomValidity(resourceStrings['bdO.Registration.error.zipcode']);
    }

    const updatedUserModel = {
      ...userModel,
      zipCode: event.target.value
    };

    event.target.reportValidity();
    setState({ ...state, ...zipCodeError });
    setUserModel(updatedUserModel);
  };

  const handleEmailInput = (event) => {
    const emailError = { emailError: false };

    if (event.target.name === 'email') {
      emailError.emailError = !event.target.validity.valid;
    }

    const updatedUserModel = {
      ...userModel,
      email: event.target.value
    };

    setState({ ...state, ...emailError });
    setUserModel(updatedUserModel);
  };

  const isFormInvalid = () => {
    const inputs = document.querySelectorAll('input');
    const selects = document.querySelectorAll('select');
    const invalidInputs = Array.from(inputs).filter((input) => !input.validity.valid);
    const invalidSelects = Array.from(selects).filter((select) => !select.validity.valid);
    const invalidElements = [...invalidInputs, ...invalidSelects];
    invalidElements.forEach((invalidElement) => {
      invalidElement.reportValidity();
    });
    const cancelSubmit = invalidElements.length > 0;

    return cancelSubmit;
  };

  const getRecaptchaToken = async (e) => {
    e.preventDefault();

    if (isFormInvalid()) {
      return;
    }

    try {
      const token = await new Promise((resolve, reject) =>
        window.grecaptcha.enterprise.ready(() => {
          window.grecaptcha.enterprise
            .execute(props.recaptchaSiteKey, {
              action: 'Register'
            })
            .then(resolve)
            .catch(reject);
        })
      );
      recaptchaRef.current.value = token;
    } catch (error) {
      console.log('error', error);
    } finally {
      hiddenSubmitRef.current.closest('form').submit();
    }
  };

  const handleEnterKeySubmit = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      getRecaptchaToken(e);
    }
  };

  useEffect(() => {
    hiddenSubmitRef.current
      .closest('form')
      .addEventListener('keydown', handleEnterKeySubmit, { once: true });
  });

  const handleDeleteAccount = (e) => {
    e.preventDefault();
    // eslint-disable-next-line no-restricted-globals
    const shouldDeleteAccount = confirm(props.deleteAccountConfirmationMessage);

    if (shouldDeleteAccount) {
      deleteRef.current.checked = true;
      deleteRef.current.value = true;
      setUserModel({ ...userModel, deleteAccount: true });
      getRecaptchaToken(e);
    }
  };

  useEffect(() => {
    if (props.errors?.length > 0) {
      errorMessagesRef.current.focus();
    }
  }, [props.errors]);

  useEffect(() => {
    const countries = countryList
      .getNames()
      .map((country) => ({
        name: country,
        value: country
      }))
      .filter((country) => country.name !== 'United States of America')
      .filter((country) => country.name !== 'Åland Islands'); // There is no mapping for this country in Kentico so it needs to be removed

    const usStates = Object.keys(STATES).map((usStateKey) => ({
      name: STATES[usStateKey],
      value: usStateKey
    }));

    const usa = countryList.getName('US');
    const alphabeticalCountries = countries.sort((a, b) => a.name.localeCompare(b.name));
    countries.unshift({ name: usa, value: usa }); // add USA to the top of the list

    setStateOptions(usStates);
    setCountryOptions(alphabeticalCountries);

    if (props.userProfileViewModel.country) {
      setSelectedCountry(
        props.userProfileViewModel.country === 'USA' ||
          props.userProfileViewModel.country === 'US' ||
          props.userProfileViewModel.country === 'United States'
          ? 'United States'
          : props.userProfileViewModel.country
      );
    }

    if (props.userProfileViewModel.state) {
      // map state to state code
      const stateCode = Object.keys(STATES).find(
        (stateKey) => STATES[stateKey] === props.userProfileViewModel.state
      );
      setSelectedState(stateCode || props.userProfileViewModel.state);
    }
  }, []);

  useEffect(() => {
    const updatedUserModel = {
      ...userModel,
      state: selectedState,
      country: selectedCountry
    };

    setUserModel(updatedUserModel);
  }, [selectedCountry, selectedState]);

  const handleGenericInput = (event) => {
    const updatedUserModel = {
      ...userModel,
      [event.target.id]: event.target.value
    };

    setUserModel({ ...updatedUserModel });
  };

  return (
    <ManageAccountFormStyled>
      {props.messages?.length > 0 && !props.isSaved && (
        <ul className="error-messages" aria-live="polite" ref={errorMessagesRef}>
          {props.messages?.map((error, index) => (
            <li key={index}>{error}</li>
          ))}
        </ul>
      )}
      <div className="form-fields-wrapper">
        {/* First Name Input */}

        <div className="form-field">
          <TextInput
            id="firstName"
            name="userProfileViewModel.firstName"
            label={resourceStrings['bdO.Registration.FirstNameLabel']}
            type="text"
            onInput={handleGenericInput}
            value={userModel.firstName}
            required
          />
        </div>

        {/* Last Name Input */}

        <div className="form-field">
          <TextInput
            id="lastName"
            name="userProfileViewModel.lastName"
            label={resourceStrings['bdO.Registration.LastNameLabel']}
            type="text"
            onInput={handleGenericInput}
            value={userModel.lastName}
            required
          />
        </div>

        {/* Email(username) Input */}

        <div className="form-field">
          <TextInput
            id="email"
            name="userProfileViewModel.email"
            label={resourceStrings['bdO.Registration.EmailUsernameLabel']}
            errorMessages={{
              custom: state.emailError ? resourceStrings['bdO.Registration.error.email'] : ''
            }}
            type="email"
            onInput={handleEmailInput}
            value={userModel.email}
            readOnly
            required
          />
        </div>

        {/* Company Input */}

        <div className="form-field">
          <TextInput
            id="company"
            name="userProfileViewModel.company"
            label={resourceStrings['bdO.Registration.CompanyLabel']}
            type="text"
            onInput={handleGenericInput}
            value={userModel.company}
            required
          />
        </div>

        {/* Job Title Input */}

        <div className="form-field">
          <TextInput
            id="jobTitle"
            name="userProfileViewModel.jobTitle"
            label={resourceStrings['bdO.Registration.JobTitleLabel']}
            type="text"
            onInput={handleGenericInput}
            value={userModel.jobTitle}
            required
          />
        </div>

        {/* Country Input */}

        <div className="form-field">
          <FormSelect
            options={countryOptions}
            name="userProfileViewModel.country"
            selectId="country"
            onChangeFunction={(e) => setSelectedCountry(e.target.value)}
            label={resourceStrings['bdO.Registration.CountryLabel']}
            placeholder={resourceStrings['bdO.Registration.CountryLabel']}
            selectedOption={selectedCountry}
            required
          />
        </div>

        {selectedCountry === 'United States of America' && (
          <>
            {/* State Input */}
            <div
              className={classNames('form-field', {
                'visibility-none': selectedCountry !== 'United States of America'
              })}
            >
              <FormSelect
                options={stateOptions}
                name="userProfileViewModel.state"
                selectId="state"
                onChangeFunction={(e) => setSelectedState(e.target.value)}
                label={resourceStrings['bdO.Registration.StateLabel']}
                placeholder={resourceStrings['bdO.Registration.StateLabel']}
                selectedOption={selectedState}
                required
              />
            </div>

            {/* Zipcode Input */}

            <div className="form-field">
              <TextInput
                id="zipCode"
                name="userProfileViewModel.zipCode"
                label={resourceStrings['bdO.Registration.ZipcodeLabel']}
                type="text"
                value={userModel.zipCode}
                onInput={handleZipCodeInput}
                onBlur={handleZipCodeInput}
                errorMessages={{
                  custom: state.zipCodeError
                    ? resourceStrings['bdO.Registration.error.zipcode']
                    : ''
                }}
                required
              />
            </div>
          </>
        )}

        {/* Hidden Recaptcha field */}
        <input hidden id="gToken" name="gToken" type="text" ref={recaptchaRef} />
        {/* Hidden Delete checkbox */}
        <input
          id="deleteAccount"
          name="deleteAccount"
          type="checkbox"
          ref={deleteRef}
          className="hidden"
        />
      </div>
      <div className="buttons-wrapper">
        <ButtonLink
          text={props.saveButtonText}
          type="primary"
          onClickFunction={getRecaptchaToken}
          isButtonTag={true}
        />
        <ButtonLink
          text={props.backButtonText ?? resourceStrings['bdO.Profile.SubscriptionsButtonText']}
          type="secondary"
          link={props.backButtonLink}
        />
        <button
          ref={hiddenSubmitRef}
          hidden
          className="hidden btn btn-primary submit"
          type="submit"
        ></button>
      </div>
      <div dangerouslySetInnerHTML={{ __html: props.summaryText }} />
      <ButtonLink
        text={props.deleteAccountButtonText}
        type="tertiary"
        onClickFunction={handleDeleteAccount}
        isButtonTag={true}
      />
      <Alert show={showSnackbar} variant={snackbarVariant}>
        {snackbarMessage}
      </Alert>
    </ManageAccountFormStyled>
  );
};

export default compose(withLocalization, withServerStyles)(ManageAccountForm);
